import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { handleEvent } from '@Events/Manager'
import { OtherEvent } from '@Events/Other'
import { MetadataContext } from '@Globals/Metadata'
import { search as searchCore, SearchResults } from '@Globals/Search/Algolia'
import { PreviousSearch, usePreviousSearches } from '@Globals/Search/PreviousSearches'
import { SearchSelection } from '@Globals/Search/Selection'

interface Search {
  active: boolean
  inputHasFocus: boolean
  previousSearches: PreviousSearch[]
  query: string
  results: SearchResults
  selection: SearchSelection
  setActive: (isActive: boolean) => void
  setInputHasFocus: (inputHasFocus: boolean) => void
  setQuery: (query: string) => void
  setSelection: (selection: SearchSelection) => void
}

interface SearchProviderProps {
  children: ReactNode
}

interface SearchState {
  active: boolean
  inputHasFocus: boolean
  query: string
  results: SearchResults
  selection: SearchSelection
}

const defaultState: SearchState = {
  active: false,
  inputHasFocus: false,
  query: '',
  results: {
    brands: [],
    categories: [],
    products: [],
  },
  selection: {
    type: 'input',
  },
}

const defaultValue: Search = {
  active: false,
  inputHasFocus: false,
  previousSearches: [],
  query: '',
  results: {
    brands: [],
    categories: [],
    products: [],
  },
  selection: {
    type: 'input',
  },
  setInputHasFocus: noop,
  setActive: noop,
  setQuery: noop,
  setSelection: noop,
}

export const SearchContext = createContext<Search>(defaultValue)
export const SearchConsumer = SearchContext.Consumer
export const SearchProvider: FC<SearchProviderProps> = ({ children }) => {
  const { store } = useContext(MetadataContext)
  const [
    {
      active: currentActive,
      inputHasFocus: currentInputHasFocus,
      query: currentQuery,
      results: currentResults,
      selection: currentSelection,
    },
    setState,
  ] = useState<SearchState>(defaultState)
  const previousSearches = usePreviousSearches()

  useEffect(
    () => {
      if (!store?.id) {
        return noop
      }

      if (currentQuery.length < 2) {
        return noop
      }

      let active = true

      const performSearch = async () => {
        if (!active) {
          return
        }

        const results = await searchCore(store?.id, currentQuery)

        if (!active) {
          return
        }

        setState((prev) => {
          const state = {
            ...prev,
            results,
          }

          if (isEqual(prev, state)) {
            return prev
          }

          return {
            ...state,
            selection: {
              type: 'input',
            },
          }
        })
      }

      performSearch().then()

      return () => {
        active = false
      }
    },
    [store?.id, currentQuery],
  )

  const resetState = useCallback(
    (query: string) => setState((prev) => {
      const state = {
        ...prev,
        ...defaultState,
        active: prev.active,
        query,
      }

      if (isEqual(prev, state)) {
        return prev
      }

      return state
    }),
    [setState],
  )

  const setActive = useCallback(
    (active: boolean) => setState(
      (prev) => {
        if (prev.active === active) {
          return prev
        }

        return {
          ...prev,
          active,
          selection: {
            type: 'input',
          },
        }
      },
    ),
    [setState],
  )

  const setQuery = useCallback(
    (query: string) => {
      if (!store?.id) {
        resetState(query)
        return
      }

      if (query.length < 2) {
        resetState(query)
        return
      }

      setState(prev => ({ ...prev, query }))
    },
    [store?.id, resetState, setState],
  )

  const setSelection = useCallback(
    (selection: SearchSelection) => setState(
      (prev) => {
        if (isEqual(prev.selection, selection)) {
          return prev
        }

        return { ...prev, selection }
      },
    ),
    [setState],
  )

  const setInputHasFocus = useCallback(
    (hasFocus: boolean) => setState(
      (prev) => {
        if (prev.inputHasFocus === hasFocus) {
          return prev
        }

        return {
          ...prev,
          inputHasFocus: hasFocus,
        }
      },
    ),
    [setState],
  )

  // Send MParticle event when search becomes active
  useEffect(() => {
    if (!currentActive) {
      return
    }

    handleEvent(new OtherEvent({
      attributes: {
        has_previous_search: !isEmpty(previousSearches),
      },
      eventName: 'search_start',
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActive])

  return (
    <SearchContext.Provider
      // TODO: avoid rerendering when no metadata changes
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        active: currentActive,
        inputHasFocus: currentInputHasFocus,
        previousSearches,
        query: currentQuery,
        results: currentResults,
        selection: currentSelection,
        setActive,
        setInputHasFocus,
        setQuery,
        setSelection,
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}
